import React from 'react';
import { Link } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { Breadcrumbs } from '../components/Breadcrumbs';
import { CheckCircle2, ShieldCheck, ArrowRight } from 'lucide-react';
import { LogoMark } from '../components/LogoMark';
export function WhoWeArePage() {
  const leadershipTeam = [{
    name: 'Matthew Butterworth',
    title: 'Founder and Chief Executive Officer',
    division: null,
    image: 'https://cdn.magicpatterns.com/uploads/1RZgczWR3wsrbNRgmZRPJ8/Matthew_Butterworth_-_Chiltern_Global_Chief_Executive_Officer_2.png',
    bio: "Matthew founded Chiltern Global and serves as Chief Executive Officer. With over 30 years of police service experience and a former Senior Detective in the Metropolitan Police, he holds a master's degree in Intelligence and Security. Matthew has embedded rigorous quality control standards across the organisation and helped establish the company as a trusted partner to military, police and security forces globally."
  }, {
    name: 'Lewis Butterworth',
    title: 'Chiltern Global Managing Director',
    division: null,
    image: 'https://cdn.magicpatterns.com/uploads/5LJCgZk4TkFkdBLi8fmrSD/Lewis_Butterworth_-_Chiltern_Global_Managing_Director_1.png',
    bio: "Lewis joined Chiltern Global after completing a bachelor's degree in politics and later earned an MSc in Marketing. He shapes and executes the company's marketing strategy while also managing daily operations, IT systems and administrative functions. Lewis played a key role in supporting the company to achieve Cyber Essentials Plus certification, strengthening the organisation's digital security posture."
  }, {
    name: 'Ollie Butterworth',
    title: 'Managing Director, Chiltern Defence',
    division: 'Chiltern Defence',
    image: 'https://cdn.magicpatterns.com/uploads/t6kkfQ5SDdHDE42z7mgBwe/Ollie_Butterworth_-_Managing_Director_of_Chiktern_Defence.png',
    bio: "Ollie leads Chiltern Defence, the registered corporation in the United Arab Emirates, and is responsible for expanding the company's international presence across the Middle East. With project management experience and fluency in Mandarin, he supports global business development and strategic partnerships, working closely with manufacturing partners on product design and production for both core range and bespoke government contracts."
  }, {
    name: 'Eddy Butterworth',
    title: 'Managing Director, Medical Warehouse Global',
    division: 'Medical Warehouse Global',
    image: 'https://cdn.magicpatterns.com/uploads/oCQPzn8UJU6H8TWBEVZrNL/Eddy_Butterworth_-_Managing_Director_of_Medical_Warehouse_Global.png',
    bio: "Eddy leads Medical Warehouse Global and has contributed to the growth of specialist knowledge and production capabilities in ballistics. Holding a bachelor's degree in mechanical engineering, he progressed into project management for specialist products within the police and security markets. Eddy combines technical expertise with an innovative approach to deliver tailored solutions for front-line services."
  }, {
    name: 'Graham Chastell',
    title: 'Business Development Lead',
    division: 'Chiltern Defence',
    image: 'https://cdn.magicpatterns.com/uploads/eNjczffixQTmnt8x7rm2Xr/Graham_Chastell.png',
    bio: 'A retired British Army Sergeant Major with over 22 years of military service, Graham leads business and product development at Chiltern Global. His operational background spans air defence, CBRN, riot control, medical training and infantry tactics. He applies this front-line expertise directly to product design and manufacturing innovation, ensuring equipment meets the practical demands of defence and police personnel.'
  }, {
    name: 'Sophia Yang',
    title: 'Procurement and Supply Chain Manager',
    division: null,
    image: 'https://cdn.magicpatterns.com/uploads/kPJoYsBoJGEG7YMrz7MsNa/Sophia_Yang.png',
    bio: 'Sophia joined Chiltern Global in 2011 and manages purchasing operations across the Far East, bringing over a decade of procurement and supply chain expertise. She is responsible for sourcing and quality assurance of materials and products for both core range and bespoke government contracts, maintaining strong relationships with local manufacturers to support cost-effective, high-quality supply solutions.'
  }, {
    name: 'Cameron McDade',
    title: 'Sales Manager',
    division: null,
    image: 'https://cdn.magicpatterns.com/uploads/aJpt3L9fGJNRogYq3VHw8o/Cameron_McDade.png',
    bio: 'Cameron has served as Sales Manager since 2022, with a focus on the police and prisons sector. He has helped secure framework agreements including YPO and BlueLight Commercial, and supported Chiltern Global in becoming the preferred supplier of the National Uniform Managed Service. With 26 years in the UK prison service and a further 8 years supplying law enforcement agencies, he brings deep procurement insight to front-line service delivery.'
  }];
  const history = [{
    year: '2005',
    title: 'Company Established',
    desc: 'Chiltern Global founded as a specialist supplier of equipment for the defence and security sectors, built on a foundation of law enforcement expertise and a commitment to quality in safety-critical products.'
  }, {
    year: '2011',
    title: 'Expanded Manufacturing Capacity',
    desc: 'Scaled operational capability and strengthened supply chain partnerships to support a growing range of manufacturing and procurement requirements across multiple product categories.'
  }, {
    year: '2012\u20132016',
    title: 'Framework Appointments',
    desc: 'Appointed to key public sector procurement frameworks including BlueLight Commercial and YPO, providing compliant and efficient routes to market for emergency services and government buyers.'
  }, {
    year: '2017\u20132021',
    title: 'International Expansion',
    desc: 'Established Chiltern Defence in the United Arab Emirates, extending international operations and logistics capability to support defence and security clients across the Middle East.'
  }, {
    year: '2022',
    title: 'Expanded Commercial Capacity',
    desc: 'Strengthened market reach and broadened sector engagement, deepening coverage across police, prisons and custodial services alongside existing defence and medical operations.'
  }, {
    year: 'Present',
    title: 'Multi-Sector Manufacturing Partner',
    desc: 'Operating across defence, police, prisons and medical sectors with offices in the United Kingdom and the United Arab Emirates, delivering specification-led equipment through structured procurement and quality-assured manufacturing.'
  }];
  const values = [{
    title: 'Integrity',
    definition: 'How we deliver value across every stage of defence and security manufacturing.',
    description: 'At Chiltern Global, integrity is not just a word but how we deliver value across every stage of defence and security manufacturing. Our commitment to acting with integrity is proven through the standards we uphold in procurement, production and client engagement.',
    points: ['Ethical conduct in every relationship: from procurement and manufacturing to client engagement, we maintain high standards of transparency and accountability', 'Quality and compliance: our multiple ISO certifications including ISO 9001:2015, ISO 14001:2015 and JOSCAR support structured processes that ensure consistency and product reliability', 'Cyber security in sensitive environments: Chiltern Global are proud holders of the Cyber Essentials Plus certificate, the UK government-backed cyber security standard', 'Continuous review and adaptation: we regularly assess our internal processes and systems to ensure we operate at the highest standard']
  }, {
    title: 'Reliability',
    definition: 'A trusted manufacturing partner in defence, policing and security for over 20 years.',
    description: 'At Chiltern Global, we pride ourselves on being a reliable manufacturing partner in the defence, policing and security sectors for over 20 years. Our clients trust us to deliver products that meet rigorous specifications, whether for individual bespoke contracts or high-volume orders.',
    points: ['End-to-end project management: we utilise a bespoke project management system that allows full visibility and control at every phase, from design through to delivery', 'Strict supervision of manufacturing: we manage every step of production to ensure we maintain high standards of quality, consistency and efficiency', 'Commitment to quality: whether for individual bespoke contracts or high-volume orders, our clients trust us to deliver products that meet rigorous defence and security specifications', 'Consistency under pressure: our processes ensure we deliver high quality, even with short deadlines and strict requirements']
  }, {
    title: 'Innovation',
    definition: 'Anticipating challenges, adopting emerging technologies and driving industry change.',
    description: 'Within the fast-moving sectors of defence, policing and security, innovation is not optional but essential. At Chiltern Global, we build relationships with clients to anticipate challenges, adopt emerging technologies and drive industry change.',
    points: ['Client-led innovation: we work closely with defence and police agencies to understand evolving operational needs, translating them into advanced specifications and solutions', 'Structured research, development and prototyping: our research and development processes explore new materials, systems and manufacturing techniques to deliver advanced and bespoke products', 'Adaptation to trends: from smart textiles to ballistic enhancements, we stay ahead of industry shifts', 'Innovation and quality combined: every concept is validated through our ISO-certified processes to ensure it meets the highest standards']
  }];
  return <>
      <PageHero label="About Us" title="Who We Are" subtitle="Meet the team behind Chiltern Global. Our people bring together operational, procurement and manufacturing expertise to deliver for front-line services." backgroundImage="https://cdn.magicpatterns.com/uploads/sCTeZG5Mo7gieHgmWAmYb6/Who_we_are.jpg" data-id="element-1488" />

      <Breadcrumbs items={[{
      label: 'About Us',
      to: '/about'
    }, {
      label: 'Who We Are'
    }]} data-id="element-1489" />

      {/* SECTION 1: Our People */}
      <section className="bg-white py-24" data-id="element-1490">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1491">
          <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-2 block" data-id="element-1492">
            Our People
          </span>
          <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-1493">
            Leadership and Team
          </h2>
          <p className="text-lg text-steel-600 max-w-3xl leading-relaxed mb-12" data-id="element-1494">
            Our leadership team brings together operational, procurement and
            technical expertise spanning military service, law enforcement,
            engineering and international supply chain management. This breadth
            of experience ensures that every product is designed, sourced and
            delivered to meet the exacting standards of front-line environments.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1495">
            {leadershipTeam.map((person, index) => <div key={index} className="bg-white border border-steel-200 rounded-sm overflow-hidden hover:border-cg-orange-600 transition-colors group flex flex-col" data-id="element-1496">
                {/* Portrait Area */}
                <div className="relative bg-slate-100 flex items-center justify-center h-72 overflow-hidden" data-id="element-1497">
                  {/* Orange military optical pattern */}
                  <svg className="absolute inset-0 w-full h-full opacity-[0.07]" viewBox="0 0 400 300" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" data-id="element-1498">
                    {/* Concentric circles (scope rings) */}
                    <circle cx="200" cy="150" r="140" stroke="#EA580C" strokeWidth="1" data-id="element-1499" />
                    <circle cx="200" cy="150" r="110" stroke="#EA580C" strokeWidth="0.8" data-id="element-1500" />
                    <circle cx="200" cy="150" r="80" stroke="#EA580C" strokeWidth="0.6" data-id="element-1501" />
                    <circle cx="200" cy="150" r="50" stroke="#EA580C" strokeWidth="0.5" data-id="element-1502" />
                    <circle cx="200" cy="150" r="20" stroke="#EA580C" strokeWidth="0.4" data-id="element-1503" />
                    {/* Crosshair lines */}
                    <line x1="200" y1="0" x2="200" y2="300" stroke="#EA580C" strokeWidth="0.5" data-id="element-1504" />
                    <line x1="0" y1="150" x2="400" y2="150" stroke="#EA580C" strokeWidth="0.5" data-id="element-1505" />
                    {/* Diagonal lines */}
                    <line x1="60" y1="10" x2="340" y2="290" stroke="#EA580C" strokeWidth="0.3" data-id="element-1506" />
                    <line x1="340" y1="10" x2="60" y2="290" stroke="#EA580C" strokeWidth="0.3" data-id="element-1507" />
                    {/* Tick marks on crosshair */}
                    <line x1="195" y1="50" x2="205" y2="50" stroke="#EA580C" strokeWidth="0.5" data-id="element-1508" />
                    <line x1="195" y1="90" x2="205" y2="90" stroke="#EA580C" strokeWidth="0.5" data-id="element-1509" />
                    <line x1="195" y1="210" x2="205" y2="210" stroke="#EA580C" strokeWidth="0.5" data-id="element-1510" />
                    <line x1="195" y1="250" x2="205" y2="250" stroke="#EA580C" strokeWidth="0.5" data-id="element-1511" />
                    <line x1="50" y1="145" x2="50" y2="155" stroke="#EA580C" strokeWidth="0.5" data-id="element-1512" />
                    <line x1="90" y1="145" x2="90" y2="155" stroke="#EA580C" strokeWidth="0.5" data-id="element-1513" />
                    <line x1="310" y1="145" x2="310" y2="155" stroke="#EA580C" strokeWidth="0.5" data-id="element-1514" />
                    <line x1="350" y1="145" x2="350" y2="155" stroke="#EA580C" strokeWidth="0.5" data-id="element-1515" />
                    {/* Corner brackets */}
                    <path d="M30 30 L30 60 M30 30 L60 30" stroke="#EA580C" strokeWidth="0.8" data-id="element-1516" />
                    <path d="M370 30 L370 60 M370 30 L340 30" stroke="#EA580C" strokeWidth="0.8" data-id="element-1517" />
                    <path d="M30 270 L30 240 M30 270 L60 270" stroke="#EA580C" strokeWidth="0.8" data-id="element-1518" />
                    <path d="M370 270 L370 240 M370 270 L340 270" stroke="#EA580C" strokeWidth="0.8" data-id="element-1519" />
                    {/* Small grid dots */}
                    <circle cx="120" cy="75" r="1.5" fill="#EA580C" data-id="element-1520" />
                    <circle cx="280" cy="75" r="1.5" fill="#EA580C" data-id="element-1521" />
                    <circle cx="120" cy="225" r="1.5" fill="#EA580C" data-id="element-1522" />
                    <circle cx="280" cy="225" r="1.5" fill="#EA580C" data-id="element-1523" />
                  </svg>
                  {person.image ? <img src={person.image} alt={person.name} className="relative z-10 w-full h-full object-contain object-center" data-id="element-1524" /> : <div className="relative z-10 w-24 h-24 bg-navy-900 rounded-full flex items-center justify-center text-2xl font-bold text-white" data-id="element-1525">
                      {person.name.split(' ').map(n => n[0]).join('')}
                    </div>}
                </div>

                {/* Content Area */}
                <div className="p-6 flex flex-col flex-1" data-id="element-1526">
                  <h3 className="font-bold text-navy-900 text-lg mb-1" data-id="element-1527">
                    {person.name}
                  </h3>
                  <span className={`text-xs font-bold text-cg-orange-600 uppercase tracking-wider block ${person.division ? 'mb-1' : 'mb-4'}`} data-id="element-1528">
                    {person.title}
                  </span>
                  {person.division && <span className="text-[10px] font-bold text-steel-400 uppercase tracking-wider block mb-4" data-id="element-1529">
                      {person.division}
                    </span>}
                  <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1530">
                    {person.bio}
                  </p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION: Our Divisions */}
      <section className="bg-white py-20 border-t border-steel-100" data-id="element-1531">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1532">
          <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-2 block" data-id="element-1533">
            Our Group
          </span>
          <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-1534">
            Our Divisions
          </h2>
          <p className="text-steel-600 max-w-3xl mb-12" data-id="element-1535">
            Chiltern Global operates through three specialist divisions, each
            focused on distinct markets and capabilities within defence,
            security and medical sectors.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1536">
            <div className="bg-slate-50 border border-steel-200 border-t-4 border-t-cg-orange-600 p-8 rounded-sm" data-id="element-1537">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1538">
                Chiltern Global
              </h3>
              <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider block mb-4" data-id="element-1539">
                Core Operations
              </span>
              <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1540">
                The parent company and primary manufacturing partner, supplying
                specialist equipment to defence, police, prisons and medical
                sectors from our United Kingdom headquarters.
              </p>
            </div>

            <div className="bg-slate-50 border border-steel-200 border-t-4 border-t-navy-900 p-8 rounded-sm" data-id="element-1541">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1542">
                Chiltern Defence
              </h3>
              <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider block mb-4" data-id="element-1543">
                International Defence
              </span>
              <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1544">
                Our registered corporation in the United Arab Emirates,
                responsible for international defence operations, business
                development and specialist product design for military
                requirements.
              </p>
            </div>

            <div className="bg-slate-50 border border-steel-200 border-t-4 border-t-steel-400 p-8 rounded-sm" data-id="element-1545">
              <h3 className="text-xl font-bold text-navy-900 mb-2" data-id="element-1546">
                Medical Warehouse Global
              </h3>
              <span className="text-xs font-bold text-cg-orange-600 uppercase tracking-wider block mb-4" data-id="element-1547">
                Medical and Ballistics
              </span>
              <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1548">
                Our specialist medical division, focused on textile-based
                medical equipment, ballistic product development and tailored
                solutions for healthcare and emergency services.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION: Certifications & Accreditations */}
      <section className="bg-white py-20 border-t border-steel-200" data-id="element-1549">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1550">
          <div className="bg-slate-50 border border-steel-200 border-l-4 border-l-cg-orange-600 p-8 md:p-10 rounded-sm" data-id="element-1551">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8" data-id="element-1552">
              <div data-id="element-1553">
                <div className="flex items-center gap-3 mb-4" data-id="element-1554">
                  <ShieldCheck className="w-6 h-6 text-cg-orange-600" data-id="element-1555" />
                  <h3 className="text-xl font-bold text-navy-900" data-id="element-1556">
                    Certifications and Accreditations
                  </h3>
                </div>
                <p className="text-sm text-steel-600 max-w-2xl mb-6 lg:mb-0" data-id="element-1557">
                  Our commitment to quality, security and environmental
                  responsibility is verified by leading accreditation bodies.
                </p>
              </div>
              <Link to="/about/certifications" className="inline-flex items-center gap-2 text-sm font-bold text-navy-900 uppercase tracking-wider hover:text-cg-orange-600 transition-colors whitespace-nowrap flex-shrink-0" data-id="element-1558">
                View All Certifications <ArrowRight className="w-4 h-4" data-id="element-1559" />
              </Link>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-8 pt-8 border-t border-steel-200" data-id="element-1560">
              {[{
              name: 'ISO 9001:2015',
              label: 'Quality Management'
            }, {
              name: 'ISO 14001:2015',
              label: 'Environmental Management'
            }, {
              name: 'Cyber Essentials Plus',
              label: 'Cyber Security'
            }, {
              name: 'JOSCAR',
              label: 'Defence Supply Chain'
            }, {
              name: 'Make UK',
              label: 'Industry Membership'
            }].map(cert => <div key={cert.name} className="bg-white border border-steel-100 p-4 rounded-sm text-center" data-id="element-1561">
                  <span className="text-sm font-bold text-navy-900 block mb-1" data-id="element-1562">
                    {cert.name}
                  </span>
                  <span className="text-[11px] text-steel-500" data-id="element-1563">
                    {cert.label}
                  </span>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2: Our History */}
      <section className="bg-slate-50 py-24 border-t border-steel-200" data-id="element-1564">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1565">
          <h2 className="text-3xl font-bold text-navy-900 mb-4" data-id="element-1566">Our History</h2>
          <p className="text-steel-600 mb-12 max-w-3xl" data-id="element-1567">
            Chiltern Global was founded in 2005 on a strong foundation of law
            enforcement and operational expertise. From a specialist defence
            supplier to a recognised manufacturing partner across multiple
            sectors, the company has grown by bringing together professionals
            with deep experience in military service, custodial environments,
            policing and public safety, ensuring that real-world operational
            insight drives every decision.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1568">
            {history.map(item => <div key={item.year} className="relative pl-8 border-l-2 border-steel-200" data-id="element-1569">
                <div className="absolute -left-[9px] top-0 w-4 h-4 rounded-full bg-cg-orange-600 border-4 border-slate-50" data-id="element-1570" />
                <span className="text-sm font-bold text-cg-orange-600 uppercase tracking-wider block mb-2" data-id="element-1571">
                  {item.year}
                </span>
                <h3 className="text-lg font-bold text-navy-900 mb-2" data-id="element-1572">
                  {item.title}
                </h3>
                <p className="text-sm text-steel-600 leading-relaxed" data-id="element-1573">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: Our Values */}
      <section className="bg-navy-900 py-24 text-white" data-id="element-1574">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1575">
          <span className="text-cg-orange-600 font-bold uppercase tracking-widest text-sm mb-2 block" data-id="element-1576">
            Our Principles
          </span>
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1577">Our Values</h2>
          <p className="text-steel-300 max-w-3xl mb-16" data-id="element-1578">
            Three principles guide every decision we make, from sourcing and
            production through to delivery and after-sales support.
          </p>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1579">
            {values.map(value => <div key={value.title} className="bg-navy-800 border border-white/10 p-8 rounded-sm flex flex-col" data-id="element-1580">
                <h3 className="text-2xl font-bold text-white mb-2" data-id="element-1581">
                  {value.title}
                </h3>
                <p className="text-cg-orange-600 font-semibold text-sm mb-6" data-id="element-1582">
                  {value.definition}
                </p>
                <p className="text-steel-300 mb-8 leading-relaxed" data-id="element-1583">
                  {value.description}
                </p>

                <h4 className="text-xs font-bold text-white uppercase tracking-wider mb-4" data-id="element-1584">
                  How this shows up in practice
                </h4>
                <ul className="space-y-3" data-id="element-1585">
                  {value.points.map((point, i) => <li key={i} className="flex items-start gap-2 text-sm text-steel-400" data-id="element-1586">
                      <CheckCircle2 className="w-4 h-4 text-cg-orange-600 flex-shrink-0 mt-0.5" data-id="element-1587" />
                      <span data-id="element-1588">{point}</span>
                    </li>)}
                </ul>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4: CTA */}
      <section className="bg-cg-orange-600 py-20" data-id="element-1589">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center" data-id="element-1590">
          <div className="flex justify-center mb-6" data-id="element-1591">
            <LogoMark size="lg" variant="dark" data-id="element-1592" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-6" data-id="element-1593">
            Partner with Chiltern Global
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-10 text-lg" data-id="element-1594">
            Discuss your manufacturing requirements with our specialist team.
          </p>
          <Link to="/contact" className="inline-flex items-center justify-center px-10 py-4 bg-white text-cg-orange-600 font-bold uppercase tracking-widest rounded-sm hover:bg-navy-900 hover:text-white transition-colors" data-id="element-1595">
            Contact Us
          </Link>
        </div>
      </section>
    </>;
}